import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../utils/api';
import {
  Plus, Search, Edit2, Trash2, Car, X, Upload, Eye,
  QrCode, Camera, AlertTriangle, ClipboardList, FileText,
  ExternalLink, ZoomIn, ChevronLeft, ChevronRight, Download,
} from 'lucide-react';
import toast from 'react-hot-toast';

const TYPES = [
  { v: 'PKW',              l: 'PKW',               e: '🚗' },
  { v: 'Transporter',      l: 'Transporter',        e: '🚐' },
  { v: 'LKW',              l: 'LKW',                e: '🚛' },
  { v: 'Traktor',          l: 'Traktor',             e: '🚜' },
  { v: 'Kuehlhaenger',     l: 'Kühlanhänger',       e: '❄️' },
  { v: 'Toilettenhaenger', l: 'Toilettenanhänger',  e: '🚽' },
  { v: 'Anhaenger',        l: 'Anhänger',            e: '🔗' },
  { v: 'Ausschankwagen',   l: 'Ausschankwagen',     e: '🍺' },
  { v: 'Sonstiges',        l: 'Sonstiges',           e: '🔧' },
];

const BRANDS = {
  PKW:              ['Alfa Romeo','Audi','BMW','Citroen','Cupra','Dacia','Fiat','Ford','Honda','Hyundai','Kia','Land Rover','Mazda','Mercedes-Benz','Mini','Nissan','Opel','Peugeot','Porsche','Renault','SEAT','Skoda','Subaru','Suzuki','Tesla','Toyota','Volkswagen','Volvo','Andere'],
  Transporter:      ['Citroen Jumper','Fiat Ducato','Ford Transit','Ford Transit Custom','Iveco Daily','Mercedes-Benz Sprinter','Mercedes-Benz Vito','Opel Movano','Opel Vivaro','Peugeot Boxer','Renault Master','Renault Trafic','VW Caddy','VW Crafter','VW T6','VW T7','Andere'],
  LKW:              ['DAF CF','DAF XF','Iveco Eurocargo','Iveco S-Way','MAN TGL','MAN TGM','MAN TGS','MAN TGX','Mercedes-Benz Actros','Mercedes-Benz Atego','Scania R','Scania S','Volvo FH','Volvo FL','Andere'],
  Traktor:          ['IHC','Case IH','Claas','Deutz-Fahr','Fendt','John Deere','Kubota','Massey Ferguson','New Holland','Steyr','Valtra','Andere'],
  Kuehlhaenger:     ['Carrier Transicold','Chereau','Frigoblock','Isopolar','Kogel Fridgo','Krone Cool Liner','Lamberet MultiTemp','Lamberet SR2','Schmitz Cargobull Frigo','Schmitz Cargobull SKO','Schwarzmueller','Thermo King','Andere'],
  Toilettenhaenger: ['Cramer Tour','Eurotoi','SaniTec','Sanitop','TipTrans','Toi Toi','Andere'],
  Anhaenger:        ['Agados','Boeckmann','Brenderup','Eduard','Hapert','Humbaur','Knott','Pongratz','Saris','Stema','Unsinn','WM Meyer','Andere'],
  Ausschankwagen:   ['Borco-Hoehn','Eigenbau','Hespeler','Hild','Mahler','Stieger','Witteler','Andere'],
  Sonstiges:        ['Andere'],
};

const FUELS = ['Diesel','Benzin','Elektro','Hybrid','Erdgas','Andere'];
const STATI = ['aktiv','inaktiv','werkstatt','verkauft','archiviert'];
const STAT_COLOR  = { aktiv:'green', inaktiv:'gray', werkstatt:'yellow', verkauft:'blue', archiviert:'purple' };
const STAT_LABEL  = { aktiv:'Aktiv', inaktiv:'Inaktiv', werkstatt:'Werkstatt', verkauft:'Verkauft', archiviert:'Archiviert' };

// Bild komprimieren vor dem Upload (max. 1200px, JPEG 80%)
async function compressImage(file, maxPx = 1200, quality = 0.80) {
  if (!file.type.startsWith('image/')) return file;
  return new Promise(resolve => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width <= maxPx && height <= maxPx) { resolve(file); return; }
      if (width > height) { height = Math.round(height * maxPx / width); width = maxPx; }
      else { width = Math.round(width * maxPx / height); height = maxPx; }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => {
        const compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
        console.log(`Bild komprimiert: ${(file.size/1024).toFixed(0)}KB → ${(compressed.size/1024).toFixed(0)}KB`);
        resolve(compressed);
      }, 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

const imgUrl = (p) => {
  if (!p) return null;
  if (p.startsWith('http') || p.startsWith('blob:')) return p;
  if (p.startsWith('/')) return p;        // /uploads/vehicles/... → direkt
  if (p.startsWith('uploads/')) return '/' + p;
  return '/uploads/vehicles/' + p;
};

// Prüft ob ein Pfad eine PDF-Datei ist
const isPdf = (p) => p && p.toLowerCase().endsWith('.pdf');
const typeInfo = (v) => TYPES.find(t => t.v === v) || { l: v || 'Sonstiges', e: '🚗' };
const displayName = (brand, model) => (!brand || brand === 'Andere') ? (model || '-') : `${brand} ${model || ''}`.trim();

const EMPTY = {
  license_plate:'', vehicle_number:'', vehicle_type:'Transporter', brand:'', model:'',
  year:'', color:'', fuel_type:'Diesel', mileage:'', weight:'', vin:'',
  last_tuev:'', next_tuev:'', last_service:'', next_service:'',
  insurance_number:'', insurance_expiry:'', status:'aktiv', notes:'', assigned_to:''
};

// ── Fullscreen Lightbox ──────────────────────────────────────────────────────
function Lightbox({ images, startIdx, vehicleId, onClose, onDeleted }) {
  const [idx, setIdx] = useState(startIdx);
  const [deleting, setDeleting] = useState(false);
  const img = images[idx];

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && idx > 0) setIdx(i => i - 1);
      if (e.key === 'ArrowRight' && idx < images.length - 1) setIdx(i => i + 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [idx, images.length, onClose]);

  const del = async () => {
    if (!window.confirm(`${img.label} wirklich löschen?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/vehicles/${vehicleId}/${img.type}`);
      toast.success(`${img.label} gelöscht`);
      onDeleted();
      onClose();
    } catch { toast.error('Fehler beim Löschen'); }
    finally { setDeleting(false); }
  };

  return (
    // Fullscreen Lightbox — portiert sich über ALLES
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#000',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header mit Safe Area für iOS/Android PWA */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px',
        paddingTop: 'max(12px, calc(env(safe-area-inset-top, 0px) + 8px))',
        paddingLeft: 'calc(16px + env(safe-area-inset-left, 0px))',
        paddingRight: 'calc(16px + env(safe-area-inset-right, 0px))',
        borderBottom: '1px solid rgba(255,255,255,.1)',
        flexShrink: 0,
        background: '#000',
        minHeight: 56,
      }}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: '.9rem' }}>
          {img.label} {images.length > 1 && `(${idx + 1}/${images.length})`}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <a
            href={img.url}
            download
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 12px', borderRadius: 8,
              background: 'rgba(255,255,255,.12)', color: '#fff',
              fontSize: '.8rem', fontWeight: 600, textDecoration: 'none',
            }}>
            <Download size={14}/> Speichern
          </a>
          <button
            onClick={del}
            disabled={deleting}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 12px', borderRadius: 8,
              background: '#dc2626', color: '#fff', border: 'none',
              fontSize: '.8rem', fontWeight: 600, cursor: 'pointer',
            }}>
            <Trash2 size={14}/> {deleting ? '...' : 'Löschen'}
          </button>
          <button
            onClick={onClose}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 36, height: 36, borderRadius: 8,
              background: 'rgba(255,255,255,.12)', color: '#fff',
              border: 'none', cursor: 'pointer',
            }}>
            <X size={18}/>
          </button>
        </div>
      </div>

      {/* Bild */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, position: 'relative',
      }}>
        {/* Prev */}
        {idx > 0 && (
          <button
            onClick={() => setIdx(i => i - 1)}
            style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              width: 40, height: 40, borderRadius: '50%',
              background: 'rgba(255,255,255,.15)', color: '#fff',
              border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
            <ChevronLeft size={22}/>
          </button>
        )}

        {isPdf(img.url) ? (
          <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', width:'100%', gap:12, padding:'0 8px', minHeight:0 }}>
            <iframe
              src={img.url}
              style={{
                width: '100%',
                flex: 1,
                minHeight: 0,
                border: 'none',
                borderRadius: 8,
                background: '#fff',
              }}
              title="Fahrzeugschein PDF"
            />
            <a href={img.url} target="_blank" rel="noreferrer"
              style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 18px', borderRadius:9, background:'rgba(255,255,255,.15)', color:'#fff', fontSize:'.83rem', fontWeight:600, textDecoration:'none', border:'1px solid rgba(255,255,255,.2)', flexShrink:0 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              PDF in neuem Tab öffnen
            </a>
          </div>
        ) : (
          <img
            src={img.url}
            alt={img.label}
            style={{ maxWidth:'100%', maxHeight:'calc(100dvh - 130px - env(safe-area-inset-top,0px) - env(safe-area-inset-bottom,0px))', width:'auto', height:'auto', objectFit:'contain', borderRadius:8, userSelect:'none', display:'block' }}
            onError={e => { e.target.style.opacity='0.3'; }}
          />
        )}

        {/* Next */}
        {idx < images.length - 1 && (
          <button
            onClick={() => setIdx(i => i + 1)}
            style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              width: 40, height: 40, borderRadius: '50%',
              background: 'rgba(255,255,255,.15)', color: '#fff',
              border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
            <ChevronRight size={22}/>
          </button>
        )}
      </div>

      {/* Klick auf Hintergrund schließt */}
      <div
        style={{ position: 'absolute', inset: 0, zIndex: -1 }}
        onClick={onClose}
      />
    </div>
  );
}

export default function Fuhrpark() {
  const [vehicles, setVehicles]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal]         = useState(false);
  const [tab, setTab]             = useState('Stammdaten');
  const [viewV, setViewV]         = useState(null);
  const [qrV, setQrV]             = useState(null);
  const [lightbox, setLightbox]   = useState(null); // { images, idx, vehicleId }
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [imgFile, setImgFile]     = useState(null);
  const [docFile, setDocFile]     = useState(null);
  const [scanning, setScanning]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [users, setUsers]         = useState([]);
  const nav = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.get('/vehicles', { params: { search, status: statusFilter } })
      .then(r => {
        let d = r.data;
        if (typeFilter) d = d.filter(v => v.vehicle_type === typeFilter);
        setVehicles(d);
      }).finally(() => setLoading(false));
  }, [search, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/users').then(r => setUsers(r.data)).catch(() => {}); }, []);

  const openCreate = () => {
    setEditing(null); setForm(EMPTY); setImgFile(null); setDocFile(null); setTab('Stammdaten'); setModal(true);
  };
  const openEdit = (v) => {
    setEditing(v);
    setForm({ ...EMPTY, ...v,
      year: v.year||'', mileage: v.mileage||'', weight: v.weight||'',
      last_tuev: v.last_tuev||'', next_tuev: v.next_tuev||'',
      last_service: v.last_service||'', next_service: v.next_service||'',
      insurance_expiry: v.insurance_expiry||'', assigned_to: v.assigned_to||''
    });
    setImgFile(null); setDocFile(null); setTab('Stammdaten'); setModal(true);
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const brands = BRANDS[form.vehicle_type] || ['Andere'];

  const openLightbox = (v, startIdx = 0) => {
    const images = [];
    if (v.image)            images.push({ url: imgUrl(v.image),            label: 'Fahrzeugfoto',  type: 'image' });
    if (v.registration_doc) images.push({ url: imgUrl(v.registration_doc), label: 'Fahrzeugschein', type: 'registration_doc' });
    if (images.length === 0) return;
    setLightbox({ images, idx: startIdx, vehicleId: v.id });
  };

  const scanDoc = async (file) => {
    setScanning(true);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result.split(',')[1]); r.onerror = rej; r.readAsDataURL(file); });
      const resp = await fetch('https://api.anthropic.com/v1/messages', {
        method:'POST', headers:{'Content-Type':'application/json','anthropic-dangerous-direct-browser-access':'true'},
        body:JSON.stringify({ model:'claude-sonnet-4-20250514', max_tokens:600,
          messages:[{ role:'user', content:[
            { type:'image', source:{type:'base64',media_type:file.type||'image/jpeg',data:b64} },
            { type:'text', text:'Fahrzeugschein analysieren. Antworte NUR mit JSON:\n{"license_plate":"","brand":"","model":"","vin":"","year":"","color":"","fuel_type":""}' }
          ]}]
        })
      });
      const data = await resp.json();
      const parsed = JSON.parse((data.content?.[0]?.text||'{}').replace(/```json|```/g,'').trim());
      setForm(p => ({ ...p,
        ...(parsed.license_plate?{license_plate:parsed.license_plate.toUpperCase()}:{}),
        ...(parsed.brand?{brand:parsed.brand}:{}),
        ...(parsed.model?{model:parsed.model}:{}),
        ...(parsed.vin?{vin:parsed.vin.toUpperCase()}:{}),
        ...(parsed.year?{year:String(parsed.year)}:{}),
        ...(parsed.color?{color:parsed.color}:{}),
        ...(parsed.fuel_type?{fuel_type:parsed.fuel_type}:{}),
      }));
      toast.success('Fahrzeugschein ausgelesen!'); setTab('Stammdaten');
    } catch { toast.error('Scan fehlgeschlagen'); }
    finally { setScanning(false); }
  };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => fd.append(k, v??''));
      // Bilder vor dem Upload komprimieren
      if (imgFile) {
        const compressed = await compressImage(imgFile);
        fd.append('image', compressed);
      }
      if (docFile) {
        const compressed = await compressImage(docFile, 1600, 0.85);
        fd.append('registration_doc', compressed);
      }
      const cfg = { headers: {'Content-Type':'multipart/form-data'} };
      if (editing) { await api.put(`/vehicles/${editing.id}`, fd, cfg); toast.success('Aktualisiert'); }
      else { await api.post('/vehicles', fd, cfg); toast.success('Fahrzeug angelegt'); }
      setModal(false); load();
    } catch(err) { toast.error(err.response?.data?.error || 'Fehler'); }
    finally { setSaving(false); }
  };

  const del = async (v) => {
    if (!window.confirm(`${v.license_plate} wirklich löschen?`)) return;
    try { await api.delete(`/vehicles/${v.id}`); toast.success('Gelöscht'); load(); }
    catch(err) { toast.error(err.response?.data?.error || 'Fehler'); }
  };

  const tuevBadge = (date) => {
    if (!date) return null;
    const d = Math.ceil((new Date(date)-new Date())/86400000);
    if (d<0) return { l:'Abgelaufen', c:'red' };
    if (d<=30) return { l:`${d}T`, c:'red' };
    if (d<=60) return { l:`${d}T`, c:'yellow' };
    return { l: new Date(date).toLocaleDateString('de-DE'), c:'green' };
  };

  const DateF = ({ label, field }) => (
    <div className="f-group">
      <label className="f-label">{label}</label>
      <div className="date-row">
        <input type="date" className="f-input" value={form[field]||''} onChange={e => f(field,e.target.value)}/>
        {form[field] && <button type="button" className="date-clr" onClick={() => f(field,'')}>✕</button>}
      </div>
    </div>
  );

  // Bildvorschau für Bearbeitungsform
  const ImagePreview = ({ vehicle, field, label, type }) => {
    const url = imgUrl(vehicle?.[field]);
    if (!url) return null;
    return (
      <div style={{
        display:'flex', alignItems:'center', gap:10, marginBottom:8,
        padding:'10px 12px', background:'var(--bg)', borderRadius:8,
        border:'1px solid var(--border)',
      }}>
        {type === 'image' ? (
          <img
            src={url} alt=""
            style={{ width:46, height:46, objectFit:'cover', borderRadius:6, cursor:'pointer', flexShrink:0 }}
            onClick={() => openLightbox(vehicle, 0)}
          />
        ) : (
          <div
            style={{ width:52, height:52, background:'var(--blue-bg)', borderRadius:6, cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}
            onClick={() => openLightbox(vehicle, vehicle.image ? 1 : 0)}>
            <FileText size={24} color="var(--blue)"/>
          </div>
        )}
        <div style={{ flex:1 }}>
          <div style={{ fontSize:'.8rem', fontWeight:600, color:'var(--green)' }}>✓ {label} vorhanden</div>
          <div style={{ fontSize:'.72rem', color:'var(--text-muted)' }}>Klicken zum Anzeigen</div>
        </div>
        <button
          type="button"
          className="btn btn-danger btn-sm"
          onClick={async () => {
            if (!window.confirm(`${label} löschen?`)) return;
            await api.delete(`/vehicles/${vehicle.id}/${type}`);
            toast.success(`${label} gelöscht`);
            load();
            // Editing aktualisieren
            const upd = await api.get(`/vehicles/${vehicle.id}`);
            setEditing(upd.data);
          }}>
          <Trash2 size={12}/> Löschen
        </button>
      </div>
    );
  };

  return (
    <Layout title="Fuhrpark">
      {/* Lightbox — außerhalb aller Modals */}
      {lightbox && (
        <Lightbox
          images={lightbox.images}
          startIdx={lightbox.idx}
          vehicleId={lightbox.vehicleId}
          onClose={() => setLightbox(null)}
          onDeleted={() => { load(); if (viewV) { api.get(`/vehicles/${viewV.id}`).then(r => setViewV(r.data)); } }}
        />
      )}

      {/* Filter */}
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <div style={{position:'relative',flex:'1 1 180px',minWidth:140}}>
          <Search size={14} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--text-muted)'}}/>
          <input className="f-input" placeholder="Kennzeichen, Marke..." value={search} onChange={e=>setSearch(e.target.value)} style={{paddingLeft:32}}/>
        </div>
        <select className="f-input" style={{width:190,flex:'0 0 auto'}} value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}>
          <option value="">Alle Typen</option>
          {TYPES.map(t=><option key={t.v} value={t.v}>{t.e} {t.l}</option>)}
        </select>
        <select className="f-input" style={{width:130,flex:'0 0 auto'}} value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}>
          <option value="">Alle Status</option>
          {STATI.map(s=><option key={s}>{s}</option>)}
        </select>
        <button className="btn btn-primary" onClick={openCreate} style={{flex:'0 0 auto'}}><Plus size={14}/> Fahrzeug</button>
      </div>

      {/* Fahrzeug-Liste — Mobile: Cards, Desktop: Tabelle */}
      <div className="card">

        {/* Desktop Tabelle */}
        <div className="tbl-wrap fg-desktop-table">
          {loading ? <div className="loading-box"><div className="spinner spinner-lg"/></div>
          : vehicles.length===0 ? (
            <div className="empty-box"><Car size={40}/><h3>Keine Fahrzeuge</h3><button className="btn btn-primary" style={{marginTop:14}} onClick={openCreate}><Plus size={14}/> Anlegen</button></div>
          ) : (
            <table>
              <thead>
                <tr><th>Fahrzeug</th><th>Typ</th><th className="hide-mob">Nr.</th><th className="hide-mob">KM</th><th>TÜV</th><th>Status</th><th>Aktionen</th></tr>
              </thead>
              <tbody>
                {vehicles.map(v => {
                  const ti   = typeInfo(v.vehicle_type);
                  const tuev = tuevBadge(v.next_tuev);
                  return (
                    <tr key={v.id}>
                      <td>
                        <div style={{display:'flex',alignItems:'center',gap:10}}>
                          {v.image ? (
                            <div style={{position:'relative',flexShrink:0,cursor:'pointer',width:38,height:38}} onClick={() => openLightbox(v, 0)}>
                              <img src={imgUrl(v.image)} alt=""
                                style={{width:36,height:36,objectFit:'cover',borderRadius:7,border:'1px solid var(--border)',display:'block'}}
                                onError={e=>e.target.style.display='none'}/>
                            </div>
                          ) : (
                            <div style={{width:38,height:38,background:'var(--bg)',borderRadius:7,display:'flex',alignItems:'center',justifyContent:'center',border:'1px solid var(--border)',flexShrink:0,fontSize:'1.2rem'}}>
                              {ti.e}
                            </div>
                          )}
                          <div>
                            <div style={{fontWeight:600,fontSize:'.83rem'}}>{displayName(v.brand,v.model)}</div>
                            <div style={{fontSize:'.72rem',color:'var(--text-muted)',fontFamily:'monospace'}}>{v.license_plate}</div>
                          </div>
                        </div>
                      </td>
                      <td><span className="badge badge-blue">{ti.e} {ti.l}</span></td>
                      <td className="hide-mob" style={{color:'var(--text-muted)'}}>{v.vehicle_number||'-'}</td>
                      <td className="hide-mob">{v.mileage?v.mileage.toLocaleString('de-DE')+' km':'-'}</td>
                      <td>{tuev?<span className={`badge badge-${tuev.c}`}>{tuev.l}</span>:'-'}</td>
                      <td>
                        {/* Status direkt änderbar */}
                        <select
                          value={v.status||'aktiv'}
                          onChange={async e => {
                            const newStatus = e.target.value;
                            try {
                              await api.put(`/vehicles/${v.id}`, { ...v, status: newStatus });
                              toast.success(`Status: ${STAT_LABEL[newStatus]}`);
                              load();
                            } catch { toast.error('Fehler'); }
                          }}
                          onClick={e => e.stopPropagation()}
                          style={{
                            padding:'4px 8px', borderRadius:7, border:'1px solid var(--border)',
                            background:'var(--bg)', color:'var(--text)',
                            fontSize:'.75rem', fontWeight:700, cursor:'pointer',
                            fontFamily:'inherit',
                          }}>
                          {STATI.map(s => <option key={s} value={s}>{STAT_LABEL[s]}</option>)}
                        </select>
                      </td>
                      <td>
                        <div style={{display:'flex',gap:3}}>
                          <button className="btn btn-ghost btn-icon btn-sm" title="Ansehen" onClick={()=>setViewV(v)}><Eye size={14}/></button>
                          <button className="btn btn-ghost btn-icon btn-sm" title="Bearbeiten" onClick={()=>openEdit(v)}><Edit2 size={14}/></button>
                          <button className="btn btn-ghost btn-icon btn-sm" title="QR-Code" onClick={()=>setQrV(v)}><QrCode size={14}/></button>
                          <button className="btn btn-ghost btn-icon btn-sm" title="Löschen" style={{color:'var(--red)'}} onClick={()=>del(v)}><Trash2 size={14}/></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Mobile Cards */}
        <div className="fg-mobile-cards">
          {loading ? <div className="loading-box"><div className="spinner spinner-lg"/></div>
          : vehicles.length===0 ? (
            <div className="empty-box"><Car size={40}/><h3>Keine Fahrzeuge</h3><button className="btn btn-primary" style={{marginTop:14}} onClick={openCreate}><Plus size={14}/> Anlegen</button></div>
          ) : vehicles.map(v => {
            const ti   = typeInfo(v.vehicle_type);
            const tuev = tuevBadge(v.next_tuev);
            return (
              <div key={v.id} style={{padding:'14px 16px',borderBottom:'1px solid var(--border)'}}>
                <div style={{display:'flex',alignItems:'flex-start',gap:12}}>
                  {/* Bild */}
                  <div
                    style={{width:64,height:64,borderRadius:10,overflow:'hidden',flexShrink:0,border:'1px solid var(--border)',background:'var(--bg)',display:'flex',alignItems:'center',justifyContent:'center',cursor:v.image?'pointer':'default'}}
                    onClick={() => v.image && openLightbox(v, 0)}>
                    {v.image
                      ? <img src={imgUrl(v.image)} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} onError={e=>e.target.style.display='none'}/>
                      : <span style={{fontSize:'1.8rem'}}>{ti.e}</span>
                    }
                  </div>

                  {/* Info */}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:'.92rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {displayName(v.brand,v.model)}
                    </div>
                    <div style={{fontSize:'.75rem',color:'var(--text-muted)',fontFamily:'monospace',marginTop:2}}>
                      {v.license_plate}
                    </div>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:6,alignItems:'center'}}>
                      <span className="badge badge-blue" style={{fontSize:'.65rem'}}>{ti.e} {ti.l}</span>
                      {tuev && <span className={`badge badge-${tuev.c}`} style={{fontSize:'.65rem'}}>TÜV {tuev.l}</span>}
                    </div>
                  </div>
                </div>

                {/* Status + Aktionen */}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:12,gap:10}}>
                  <select
                    value={v.status||'aktiv'}
                    onChange={async e => {
                      const ns = e.target.value;
                      try {
                        await api.put(`/vehicles/${v.id}`, { ...v, status: ns });
                        toast.success(`Status: ${STAT_LABEL[ns]}`);
                        load();
                      } catch { toast.error('Fehler'); }
                    }}
                    style={{
                      padding:'8px 12px', borderRadius:8,
                      border:'1.5px solid var(--border)',
                      background:'var(--bg-input)', color:'var(--text)',
                      fontSize:'.82rem', fontWeight:700, cursor:'pointer',
                      fontFamily:'inherit', flex:1,
                    }}>
                    {STATI.map(s => <option key={s} value={s}>{STAT_LABEL[s]}</option>)}
                  </select>
                  <div style={{display:'flex',gap:6,flexShrink:0}}>
                    <button className="btn btn-secondary btn-sm" style={{padding:'8px 10px'}} onClick={()=>setViewV(v)}><Eye size={14}/></button>
                    <button className="btn btn-secondary btn-sm" style={{padding:'8px 10px'}} onClick={()=>openEdit(v)}><Edit2 size={14}/></button>
                    <button className="btn btn-ghost btn-sm" style={{padding:'8px 10px',color:'var(--text-muted)'}} onClick={()=>setQrV(v)}><QrCode size={14}/></button>
                    <button className="btn btn-danger btn-sm" style={{padding:'8px 10px'}} onClick={()=>del(v)}><Trash2 size={14}/></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .fg-desktop-table { display: block; }
        .fg-mobile-cards  { display: none; }
        @media (max-width: 700px) {
          .fg-desktop-table { display: none !important; }
          .fg-mobile-cards  { display: block !important; }
        }
      `}</style>

      {/* QR Modal */}
      {qrV && (
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setQrV(null)}>
          <div className="modal modal-sm">
            <div className="modal-head"><span className="modal-head-title">QR: {qrV.license_plate}</span><button className="btn btn-ghost btn-icon" onClick={()=>setQrV(null)}><X size={16}/></button></div>
            <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:10}}>
              <button className="btn btn-primary" style={{justifyContent:'flex-start',padding:'14px 16px'}} onClick={()=>{setQrV(null);nav(`/fuhrpark/${qrV.id}/qr/schaden`);}}><AlertTriangle size={18}/><div style={{marginLeft:8,textAlign:'left'}}><div style={{fontWeight:700}}>Schadensmeldung</div><div style={{fontSize:'.75rem',opacity:.85}}>Schaden direkt melden</div></div></button>
              <button className="btn btn-secondary" style={{justifyContent:'flex-start',padding:'14px 16px'}} onClick={()=>{setQrV(null);nav(`/fuhrpark/${qrV.id}/qr/packliste`);}}><ClipboardList size={18}/><div style={{marginLeft:8,textAlign:'left'}}><div style={{fontWeight:700}}>Packliste</div></div></button>
            </div>
          </div>
        </div>
      )}

      {/* Ansicht Modal */}
      {viewV && !lightbox && (
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setViewV(null)}>
          <div className="modal">
            <div className="modal-head">
              <span className="modal-head-title">{typeInfo(viewV.vehicle_type).e} {viewV.license_plate}</span>
              <div style={{display:'flex',gap:6}}>
                <button className="btn btn-secondary btn-sm" onClick={()=>{setViewV(null);setQrV(viewV);}}><QrCode size={13}/> QR</button>
                <button className="btn btn-ghost btn-icon" onClick={()=>setViewV(null)}><X size={16}/></button>
              </div>
            </div>
            <div className="modal-body">
              {/* Galerie */}
              {(viewV.image || viewV.registration_doc) && (
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:'.68rem',fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Bilder & Dokumente</div>
                  <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                    {viewV.image && (
                      <div
                        onClick={() => openLightbox(viewV, 0)}
                        style={{cursor:'pointer',borderRadius:8,overflow:'hidden',border:'1px solid var(--border)',width:100,height:76,position:'relative',flexShrink:0}}>
                        <img src={imgUrl(viewV.image)} alt=""
                          style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}
                          onError={e=>e.target.style.display='none'}/>
                        <div style={{position:'absolute',inset:0,background:'rgba(0,0,0,0)',display:'flex',alignItems:'center',justifyContent:'center',transition:'background 200ms'}}
                          onMouseEnter={e=>e.currentTarget.style.background='rgba(0,0,0,.4)'}
                          onMouseLeave={e=>e.currentTarget.style.background='rgba(0,0,0,0)'}>
                          <ZoomIn size={20} color="#fff"/>
                        </div>
                        <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'4px 8px',background:'rgba(0,0,0,.6)',color:'#fff',fontSize:'.68rem',fontWeight:600}}>Foto</div>
                      </div>
                    )}
                    {viewV.registration_doc && (
                      <div
                        onClick={() => openLightbox(viewV, viewV.image ? 1 : 0)}
                        style={{
                          cursor:'pointer', borderRadius:10, overflow:'hidden',
                          border:'2px solid var(--blue)', width:100, height:76,
                          position:'relative', flexShrink:0, background:'var(--blue-bg)',
                        }}>
                        <img
                          src={imgUrl(viewV.registration_doc)} alt="Fahrzeugschein"
                          style={{width:'100%',height:'100%',objectFit:'cover',display: isPdf(viewV.registration_doc)?'none':'block'}}
                          onError={e => { e.target.style.display='none'; }}
                        />
                        {isPdf(viewV.registration_doc) && (
                          <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:4}}>
                            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/></svg>
                            <span style={{fontSize:'.6rem',color:'white',fontWeight:700,letterSpacing:'.05em'}}>PDF</span>
                          </div>
                        )}
                        <div style={{
                          position:'absolute', bottom:0, left:0, right:0,
                          padding:'4px 7px', background:'rgba(37,99,235,.88)',
                          color:'#fff', fontSize:'.65rem', fontWeight:700,
                          display:'flex', alignItems:'center', gap:4,
                        }}>
                          <FileText size={9} color="#fff"/>
                          Fahrzeugschein
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="form-grid g2">
                {[
                  ['Typ',`${typeInfo(viewV.vehicle_type).e} ${typeInfo(viewV.vehicle_type).l}`],
                  ['Marke/Modell', displayName(viewV.brand,viewV.model)],
                  ['Nr.',viewV.vehicle_number||'-'],['Baujahr',viewV.year||'-'],
                  ['Farbe',viewV.color||'-'],['Kraftstoff',viewV.fuel_type||'-'],
                  ['KM-Stand',viewV.mileage?viewV.mileage.toLocaleString('de-DE')+' km':'-'],['Gewicht',viewV.weight||'-'],
                  ['Letzter TÜV',viewV.last_tuev?new Date(viewV.last_tuev).toLocaleDateString('de-DE'):'-'],
                  ['Nächster TÜV',viewV.next_tuev?new Date(viewV.next_tuev).toLocaleDateString('de-DE'):'-'],
                  ['Letzte Inspektion',viewV.last_service?new Date(viewV.last_service).toLocaleDateString('de-DE'):'-'],
                  ['Status',viewV.status],
                ].map(([l,v])=>(
                  <div key={l}>
                    <div style={{fontSize:'.65rem',fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:2}}>{l}</div>
                    <div style={{fontWeight:500,fontSize:'.85rem'}}>{v}</div>
                  </div>
                ))}
              </div>
              {viewV.notes && <p style={{fontSize:'.82rem',color:'var(--text-muted)',marginTop:12}}>{viewV.notes}</p>}
            </div>
            <div className="modal-foot">
              <button className="btn btn-secondary" onClick={()=>setViewV(null)}>Schließen</button>
              <button className="btn btn-primary" onClick={()=>{setViewV(null);openEdit(viewV);}}>Bearbeiten</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Create Modal */}
      {modal && !lightbox && (
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-head">
              <span className="modal-head-title">{editing?'Fahrzeug bearbeiten':'Neues Fahrzeug anlegen'}</span>
              <button className="btn btn-ghost btn-icon" onClick={()=>setModal(false)}><X size={16}/></button>
            </div>
            <div className="tabs" style={{margin:'0 16px'}}>
              <button className={`tab-btn ${tab==='Stammdaten'?'active':''}`} onClick={()=>setTab('Stammdaten')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                Stammdaten
              </button>
              <button className={`tab-btn ${tab==='Fahrzeugschein'?'active':''}`} onClick={()=>setTab('Fahrzeugschein')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                Fahrzeugschein
                {editing?.registration_doc && <span style={{width:7,height:7,borderRadius:'50%',background:'var(--green)',display:'inline-block'}}/>}
              </button>
            </div>
            <form onSubmit={save} style={{display:'contents'}}>
              <div className="modal-body">
                {tab==='Stammdaten' && (
                  <div className="form-grid">
                    <div className="form-grid g3">
                      <div className="f-group"><label className="f-label">Fahrzeugtyp *</label><select className="f-input" value={form.vehicle_type} onChange={e=>{f('vehicle_type',e.target.value);f('brand','');}} required>{TYPES.map(t=><option key={t.v} value={t.v}>{t.e} {t.l}</option>)}</select></div>
                      <div className="f-group"><label className="f-label">Kennzeichen *</label><input className="f-input" value={form.license_plate} onChange={e=>f('license_plate',e.target.value.toUpperCase())} required placeholder="VIE-AB 123" style={{fontFamily:'monospace',fontWeight:700}}/></div>
                      <div className="f-group"><label className="f-label">Fahrzeugnummer</label><input className="f-input" value={form.vehicle_number} onChange={e=>f('vehicle_number',e.target.value)}/></div>
                    </div>
                    <div className="form-grid g2">
                      <div className="f-group"><label className="f-label">Marke *</label><select className="f-input" value={form.brand} onChange={e=>f('brand',e.target.value)} required><option value="">-- Wählen --</option>{brands.map(b=><option key={b} value={b}>{b}</option>)}</select></div>
                      <div className="f-group"><label className="f-label">Modell *</label><input className="f-input" value={form.model} onChange={e=>f('model',e.target.value)} required/></div>
                    </div>
                    <div className="form-grid g3">
                      <div className="f-group"><label className="f-label">Baujahr</label><input type="number" className="f-input" min={1950} max={2030} value={form.year} onChange={e=>f('year',e.target.value)}/></div>
                      <div className="f-group"><label className="f-label">Farbe</label><input className="f-input" value={form.color} onChange={e=>f('color',e.target.value)}/></div>
                      <div className="f-group"><label className="f-label">Kraftstoff</label><select className="f-input" value={form.fuel_type} onChange={e=>f('fuel_type',e.target.value)}>{FUELS.map(t=><option key={t}>{t}</option>)}</select></div>
                    </div>
                    <div className="form-grid g3">
                      <div className="f-group"><label className="f-label">KM-Stand</label><input type="number" className="f-input" min={0} value={form.mileage} onChange={e=>f('mileage',e.target.value)}/></div>
                      <div className="f-group"><label className="f-label">Gewicht/zGG</label><input className="f-input" value={form.weight} onChange={e=>f('weight',e.target.value)} placeholder="z.B. 3.500 kg"/></div>
                      <div className="f-group"><label className="f-label">FIN/VIN</label><input className="f-input" value={form.vin} onChange={e=>f('vin',e.target.value.toUpperCase())} style={{fontFamily:'monospace',fontSize:'.78rem'}}/></div>
                    </div>
                    <div className="form-grid g2">
                      <DateF label="Letzter TÜV" field="last_tuev"/>
                      <DateF label="Nächster TÜV" field="next_tuev"/>
                      <DateF label="Letzte Inspektion" field="last_service"/>
                      <DateF label="Nächste Inspektion" field="next_service"/>
                      <div className="f-group"><label className="f-label">Versicherungsnr.</label><input className="f-input" value={form.insurance_number} onChange={e=>f('insurance_number',e.target.value)}/></div>
                      <DateF label="Vers. Ablauf" field="insurance_expiry"/>
                    </div>
                    <div className="form-grid g2">
                      <div className="f-group"><label className="f-label">Status</label><select className="f-input" value={form.status} onChange={e=>f('status',e.target.value)}>{STATI.map(s=><option key={s}>{s}</option>)}</select></div>
                      <div className="f-group"><label className="f-label">Zugewiesen an</label><select className="f-input" value={form.assigned_to} onChange={e=>f('assigned_to',e.target.value)}><option value="">Keiner</option>{users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}</select></div>
                    </div>
                    <div className="f-group"><label className="f-label">Notizen</label><textarea className="f-input" value={form.notes} onChange={e=>f('notes',e.target.value)} rows={2}/></div>
                    <div className="f-group">
                      <label className="f-label">Fahrzeugfoto</label>
                      {editing && <ImagePreview vehicle={editing} field="image" label="Fahrzeugfoto" type="image"/>}
                      <div className="upload-zone" onClick={()=>document.getElementById('img-inp').click()}>
                        {imgFile?<span style={{color:'var(--green)',fontWeight:600}}>✓ {imgFile.name}</span>:<><Upload size={18}/><span style={{fontSize:'.8rem'}}>Neues Foto hochladen</span></>}
                        <input id="img-inp" type="file" accept="image/*" style={{display:'none'}} onChange={e=>setImgFile(e.target.files[0])}/>
                      </div>
                    </div>
                  </div>
                )}
                {tab==='Fahrzeugschein' && (
                  <div className="form-grid">
                    <div className="alert alert-info"><Camera size={16}/><div><strong>KI-Scan:</strong> Fahrzeugschein fotografieren — Daten werden automatisch ausgelesen.</div></div>
                    {editing && <ImagePreview vehicle={editing} field="registration_doc" label="Fahrzeugschein" type="registration_doc"/>}
                    <div className="upload-zone" onClick={()=>document.getElementById('doc-inp').click()} style={{background:docFile?'var(--green-bg)':undefined}}>
                      {scanning?<><div className="spinner" style={{margin:'0 auto 8px'}}/><div style={{fontWeight:600}}>KI liest aus...</div></>
                      :docFile?<div style={{color:'var(--green)'}}><div style={{fontSize:'1.5rem'}}>✓</div><div style={{fontWeight:700}}>{docFile.name}</div></div>
                      :<><Camera size={32}/><div style={{fontWeight:600}}>Fahrzeugschein fotografieren / hochladen</div></>}
                      <input id="doc-inp" type="file" accept="image/*,application/pdf" capture="environment" style={{display:'none'}}
                        onChange={e=>{const file=e.target.files[0];if(file){setDocFile(file);if(file.type.startsWith('image/'))scanDoc(file);}}}/>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-foot">
                <button type="button" className="btn btn-secondary" onClick={()=>setModal(false)}>Abbrechen</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving&&<span className="spinner" style={{width:14,height:14,borderWidth:2}}/>}
                  {editing?'Speichern':'Anlegen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
